/**
 * community-gallery-autoplay.js
 *
 * Auto-advances the "Inspired by You" community gallery, reusing the
 * existing page-based scroll-snap navigation rather than introducing a
 * parallel state machine. One controller instance per matching root.
 *
 * Activation:
 *   <section
 *     data-community-gallery-autoplay
 *     data-autoplay-enabled="true"
 *     data-autoplay-interval="5500">
 *
 * Pauses on: hover (hover-capable pointers only), focus-within, manual
 * touch/drag scroll, hidden tab, and when less than 50% of the gallery is
 * in the viewport. Bails out entirely when the visitor prefers reduced
 * motion, or when the merchant disables autoplay from the theme editor.
 * Pages loop continuously: last page advances back to the first.
 */
(function () {
  'use strict';

  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  var HOVER_CAPABLE_QUERY = '(hover: hover) and (pointer: fine)';
  var GRACE_PERIOD_MS = 8000;
  var RESUME_DELAY_MS = 800;
  var AUTO_SCROLL_FLAG_MS = 900;
  var INTERSECTION_THRESHOLD = 0.5;

  function SoCommunityGalleryAutoplay(root) {
    this.root = root;
    this.destroyed = false;

    var rawInterval = parseInt(root.dataset.autoplayInterval, 10);
    this.interval = isFinite(rawInterval) && rawInterval > 0 ? rawInterval : 5500;
    this.enabled = root.dataset.autoplayEnabled !== 'false';

    this.track = root.querySelector('[data-community-track]');
    this.pages = this.track
      ? Array.prototype.slice.call(this.track.querySelectorAll('[data-gallery-page]'))
      : [];
    if (!this.track || this.pages.length < 2) return;

    this.hoverTarget = root.querySelector('.community-gallery__viewport') || root;

    this.timer = null;
    this.resumeTimer = null;
    this.autoScrollFlagTimer = null;
    this.paused = false;
    this.isAutoScroll = false;
    this.isVisible = false;
    this.observer = null;

    this.reducedMotionQuery = window.matchMedia
      ? window.matchMedia(REDUCED_MOTION_QUERY)
      : null;
    this.hoverCapableQuery = window.matchMedia
      ? window.matchMedia(HOVER_CAPABLE_QUERY)
      : null;

    this.handlers = {};
    this.bindHandlers();
    this.attachListeners();

    if (this.canRun()) {
      this.observeVisibility();
    }

    root.__soCommunityGalleryAutoplay = this;
  }

  SoCommunityGalleryAutoplay.prototype.canRun = function () {
    if (!this.enabled) return false;
    if (this.reducedMotionQuery && this.reducedMotionQuery.matches) return false;
    return true;
  };

  SoCommunityGalleryAutoplay.prototype.hasHoverCapability = function () {
    return !this.hoverCapableQuery || this.hoverCapableQuery.matches;
  };

  SoCommunityGalleryAutoplay.prototype.bindHandlers = function () {
    var self = this;
    this.handlers.mouseEnter = function () { self.pauseIndefinite(); };
    this.handlers.mouseLeave = function () { self.resumeAfter(RESUME_DELAY_MS); };
    this.handlers.focusIn = function () { self.pauseIndefinite(); };
    this.handlers.focusOut = function () {
      if (!self.root.contains(document.activeElement)) {
        self.resumeAfter(RESUME_DELAY_MS);
      }
    };
    this.handlers.trackScroll = function () {
      if (self.isAutoScroll) return;
      self.pauseFor(GRACE_PERIOD_MS);
    };
    this.handlers.visibilityChange = function () {
      if (document.visibilityState === 'hidden') {
        self.stopTimer();
      } else if (self.canRun() && self.isVisible) {
        self.startTimer();
      }
    };
    this.handlers.reducedMotionChange = function () {
      if (self.canRun()) {
        self.observeVisibility();
        if (self.isVisible && document.visibilityState !== 'hidden') self.startTimer();
      } else {
        self.stopTimer();
      }
    };
    this.handlers.hoverCapabilityChange = function () {
      self.detachHoverListeners();
      self.attachHoverListeners();
    };
  };

  SoCommunityGalleryAutoplay.prototype.attachHoverListeners = function () {
    if (!this.hasHoverCapability()) return;
    this.hoverTarget.addEventListener('mouseenter', this.handlers.mouseEnter);
    this.hoverTarget.addEventListener('mouseleave', this.handlers.mouseLeave);
    this.hoverListenersAttached = true;
  };

  SoCommunityGalleryAutoplay.prototype.detachHoverListeners = function () {
    if (!this.hoverListenersAttached) return;
    this.hoverTarget.removeEventListener('mouseenter', this.handlers.mouseEnter);
    this.hoverTarget.removeEventListener('mouseleave', this.handlers.mouseLeave);
    this.hoverListenersAttached = false;
  };

  SoCommunityGalleryAutoplay.prototype.attachListeners = function () {
    this.attachHoverListeners();

    this.root.addEventListener('focusin', this.handlers.focusIn);
    this.root.addEventListener('focusout', this.handlers.focusOut);

    this.track.addEventListener('scroll', this.handlers.trackScroll, { passive: true });

    document.addEventListener('visibilitychange', this.handlers.visibilityChange);

    if (this.reducedMotionQuery && typeof this.reducedMotionQuery.addEventListener === 'function') {
      this.reducedMotionQuery.addEventListener('change', this.handlers.reducedMotionChange);
    }
    if (this.hoverCapableQuery && typeof this.hoverCapableQuery.addEventListener === 'function') {
      this.hoverCapableQuery.addEventListener('change', this.handlers.hoverCapabilityChange);
    }
  };

  SoCommunityGalleryAutoplay.prototype.observeVisibility = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) {
      this.isVisible = true;
      if (document.visibilityState !== 'hidden') this.startTimer();
      return;
    }
    if (this.observer) return;
    this.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        self.isVisible = entry.isIntersecting && entry.intersectionRatio >= INTERSECTION_THRESHOLD;
        if (self.isVisible && self.canRun() && document.visibilityState !== 'hidden') {
          self.startTimer();
        } else {
          self.stopTimer();
        }
      });
    }, { threshold: [0, INTERSECTION_THRESHOLD, 1] });
    this.observer.observe(this.root);
  };

  SoCommunityGalleryAutoplay.prototype.getActivePage = function () {
    var scrollLeft = this.track.scrollLeft;
    var trackOffset = this.track.offsetLeft;
    var best = 0;
    var bestDist = Infinity;
    for (var i = 0; i < this.pages.length; i++) {
      var dist = Math.abs(this.pages[i].offsetLeft - trackOffset - scrollLeft);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  };

  SoCommunityGalleryAutoplay.prototype.advance = function () {
    var self = this;
    var next = (this.getActivePage() + 1) % this.pages.length;
    this.isAutoScroll = true;
    this.pages[next].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    clearTimeout(this.autoScrollFlagTimer);
    this.autoScrollFlagTimer = setTimeout(function () {
      self.isAutoScroll = false;
    }, AUTO_SCROLL_FLAG_MS);
  };

  SoCommunityGalleryAutoplay.prototype.startTimer = function () {
    if (this.destroyed) return;
    if (!this.canRun()) return;
    if (this.timer !== null) return;
    var self = this;
    this.timer = setInterval(function () {
      if (!self.paused) self.advance();
    }, this.interval);
  };

  SoCommunityGalleryAutoplay.prototype.stopTimer = function () {
    if (this.timer === null) return;
    clearInterval(this.timer);
    this.timer = null;
  };

  SoCommunityGalleryAutoplay.prototype.pauseIndefinite = function () {
    this.paused = true;
    clearTimeout(this.resumeTimer);
  };

  SoCommunityGalleryAutoplay.prototype.resumeAfter = function (delay) {
    var self = this;
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(function () { self.paused = false; }, delay);
  };

  SoCommunityGalleryAutoplay.prototype.pauseFor = function (duration) {
    var self = this;
    this.paused = true;
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(function () { self.paused = false; }, duration);
  };

  SoCommunityGalleryAutoplay.prototype.destroy = function () {
    if (this.destroyed) return;
    this.destroyed = true;

    this.stopTimer();
    clearTimeout(this.resumeTimer);
    clearTimeout(this.autoScrollFlagTimer);

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.detachHoverListeners();
    this.root.removeEventListener('focusin', this.handlers.focusIn);
    this.root.removeEventListener('focusout', this.handlers.focusOut);
    this.track.removeEventListener('scroll', this.handlers.trackScroll);
    document.removeEventListener('visibilitychange', this.handlers.visibilityChange);

    if (this.reducedMotionQuery && typeof this.reducedMotionQuery.removeEventListener === 'function') {
      this.reducedMotionQuery.removeEventListener('change', this.handlers.reducedMotionChange);
    }
    if (this.hoverCapableQuery && typeof this.hoverCapableQuery.removeEventListener === 'function') {
      this.hoverCapableQuery.removeEventListener('change', this.handlers.hoverCapabilityChange);
    }

    if (this.root.__soCommunityGalleryAutoplay === this) {
      delete this.root.__soCommunityGalleryAutoplay;
    }
  };

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-community-gallery-autoplay]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__soCommunityGalleryAutoplay) return;
      new SoCommunityGalleryAutoplay(root);
    });
  }

  function teardownIn(scope) {
    if (!scope) return;
    var roots = scope.matches && scope.matches('[data-community-gallery-autoplay]')
      ? [scope]
      : Array.prototype.slice.call(scope.querySelectorAll('[data-community-gallery-autoplay]'));
    roots.forEach(function (root) {
      if (root.__soCommunityGalleryAutoplay) root.__soCommunityGalleryAutoplay.destroy();
    });
  }

  function scheduleInit(scope) {
    var run = function () { initAll(scope); };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      window.setTimeout(run, 0);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scheduleInit();
    });
  } else {
    scheduleInit();
  }

  document.addEventListener('shopify:section:load', function (event) {
    scheduleInit(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    teardownIn(event.target);
  });
  document.addEventListener('shopify:section:reorder', function (event) {
    teardownIn(event.target);
    scheduleInit(event.target);
  });

  window.SoCommunityGalleryAutoplay = SoCommunityGalleryAutoplay;
})();
